// Imports
import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { message } from "antd";
import "tippy.js/dist/tippy.css";
import { SyncOutlined } from "@ant-design/icons";
import LoadingSpinner from "../../../components/reusable/LoadingSpinner";
import ConfirmationModal from "../../../components/reusable/ConfirmationModal";
import DateRangePicker from "../../../components/reusable/DateRangePicker/DateRangePicker";
import dayjs from "dayjs";
import CampaignTable from "../../../components/reusable/CampaignTable/CampaignTable";
import StyledInput from "../../../components/reusable/StyledInput";
import SelectMenu from "src/components/common/SelectMenu/SelectMenu";
import { Trash2, Download, Archive, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "src/hooks/UseLanguage";
import { formatNumber, getNumberLocale, wrapBidiIsolate } from "src/utils/numberFormat";
import { useDynamicTextTranslator } from "src/utils/dynamicTextTranslation";
import { DashboardNarrativePanel } from "src/components/common";
import { useDashboardNarrative } from "src/hooks/UseDashboardNarrative";
import { useAsyncResource } from "src/hooks/UseAsyncResource";
import {
  campaignPerformanceService,
  type CampaignComparisonData,
  type CampaignPagination,
} from "src/services/CampaignPerformanceAPI";
import {
  engagementOverviewService,
  type EngagementAchievementsData,
} from "src/services/EngagementOverviewAPI";
import {
  BusinessImpactSection,
  mapAchievements,
  readFormattedText,
  readNumericValue,
} from "src/layouts/Engagements/Overview/Index";
import { getApiLangFromI18n } from "src/utils/apiLang";
import { getThrownApiDetail } from "src/utils/showApiDetailToast";
import { CampaignDraftAPI } from "src/services/CampaignDraftAPI";
import { isDraftCampaignId } from "src/services/CampaignDraftTypes";
import { getDraftEditorRoute } from "./shared/CampaignDraftUtils";
import CampaignPerformance from "./CampaignPerformance";
import CreateCampaignMenu from "./Dashboard/CreateCampaignMenu";
import CampaignViewModal, { type CampaignViewSummary } from "./Dashboard/CampaignViewModal";
import CampaignComparisonChart from "./Dashboard/CampaignComparisonChart";
import {
  CAMPAIGN_LIST_PAGE_SIZE,
  campaignDashboardTranslationKeys,
  metricOptionsBase,
} from "./Dashboard/CampaignDashboardConstants";
import type { Campaign, CampaignDashboardText } from "./Dashboard/CampaignDashboardTypes";
import {
  collectFilterOptions,
  exportToCSV,
  getCampaignSource,
  getCampaignSourceLabel,
  getDraftId,
  getModalProps,
  mapCampaignListRow,
  matchesStatusFilter,
  normalizeBadgeVariant,
} from "./Dashboard/CampaignDashboardUtils";
import CampaignMetricsPanel from "./Dashboard/CampaignMetricsPanel";
import { buildCampaignTableColumns } from "./Dashboard/CampaignTableColumns";
import CampaignTablePagination from "./Dashboard/CampaignTablePagination";
import "../Overview/Overview.scss";

// Component
const CampaignDashboard: React.FC = () => {
  const { t: translate, i18n } = useTranslation();
  useLanguage();
  const lang = getApiLangFromI18n(i18n);
  const t: CampaignDashboardText = useMemo(
    () =>
      campaignDashboardTranslationKeys.reduce((acc, key) => {
        acc[key] = translate(`campaignDashboard.${key}`);
        return acc;
      }, {} as CampaignDashboardText),
    [translate, i18n.resolvedLanguage, i18n.language],
  );
  const metricOptions = useMemo(
    () =>
      metricOptionsBase.map(metric => ({
        ...metric,
        label: t[metric.labelKey as keyof typeof t] as string,
      })),
    [t],
  );
  const isArabic = i18n.language?.startsWith("ar");
  const numberLocale = useMemo(
    () => getNumberLocale(i18n.language),
    [i18n.language]
  );
  const dateFormatter = useMemo(
    () => ({
      format: (date: Date) => {
        const dd = String(date.getDate()).padStart(2, "0");
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        return `${dd}-${mm}-${date.getFullYear()}`;
      },
    }),
    []
  );
  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(numberLocale, {
        hour: "numeric",
        minute: "2-digit",
      }),
    [numberLocale]
  );
  const formatDateTime = (dateString: string) => {
    if (!dateString || dateString === "-" || dateString.trim() === "") {
      return "-";
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "-";
    }
    return `${dateFormatter.format(date)}\n${timeFormatter.format(date)}`;
  };

  const formatInteger = useCallback(
    (value: number) => formatNumber(value, {}, i18n.language),
    [i18n.language]
  );

  const formatPercentValue = useCallback(
    (value: number, fractionDigits = 2) => {
      const percentText = `${formatNumber(
        value,
        {
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits,
        },
        i18n.language
      )}%`;
      return isArabic ? wrapBidiIsolate(percentText) : percentText;
    },
    [i18n.language, isArabic]
  );

  // State
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedMetrics] = useState<string[]>([
    "delivered",
    "openRate",
    "clickRate",
    "bounceRate",
    "conversionRate",
    "unsubscribeRate",
  ]);
  const selectAllRef = useRef<HTMLInputElement>(null);
 const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [startDate, setStartDate] = useState(dayjs().subtract(3, "month").format("YYYY-MM-DD"));
const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));

  const [showArchived, setShowArchived] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    null
  );
  const [viewCampaign, setViewCampaign] = useState<CampaignViewSummary | null>(null);
  const [performanceCampaign, setPerformanceCampaign] = useState<{
    campaign_id: string;
    campaign_name: string;
  } | null>(null);
  const [pendingActionIds, setPendingActionIds] = useState<string[]>([]);
  const [actionType, setActionType] = useState<
    "archive" | "delete" | "unarchive" | null
  >(null);
  const [actionLoading, setActionLoading] = useState(false);
  // Header/toolbar filters. Empty string = "all" for each.
  const [campaignFilter, setCampaignFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  // Campaign Comparison: campaign_ids to plot. Empty = plot everything in scope.
  const [comparisonIds, setComparisonIds] = useState<string[]>([]);
  const [comparisonSources, setComparisonSources] = useState<string[]>([]);
  const [listPagination, setListPagination] = useState<CampaignPagination | null>(null);
  const pageSize = 6;

  const dynamicTextPool = useMemo(
    () =>
      campaigns.flatMap(campaign => [
        campaign.campaign_name,
        campaign.audience,
      ]),
    [campaigns]
  );

  const translateDynamicLabel = useDynamicTextTranslator(
    dynamicTextPool,
    i18n.language
  );

  // Derived Data
  // Campaigns in the current Active/Archive scope, before the dropdown filters.
  // Filter options are derived from this so they can never offer a value that
  // contradicts the selected tab (e.g. "archive" while viewing Active).
  const scopedCampaigns = useMemo(() => {
    return campaigns.filter(c =>
      showArchived ? c.status === "archive" : c.status !== "archive"
    );
  }, [campaigns, showArchived]);

  const filteredCampaigns = useMemo(() => {
    return scopedCampaigns.filter(c => {
      if (campaignFilter && c.campaign_name !== campaignFilter) {
        return false;
      }
      if (
        sourceFilter &&
        getCampaignSource(c).toLowerCase() !== sourceFilter.toLowerCase()
      ) {
        return false;
      }
      if (statusFilter && !matchesStatusFilter(c, statusFilter)) {
        return false;
      }
      return true;
    });
  }, [scopedCampaigns, campaignFilter, sourceFilter, statusFilter]);

  const campaignNameOptions = useMemo(
    () => [
      { value: "", label: t.filterAllCampaigns },
      ...Array.from(new Set(scopedCampaigns.map(c => c.campaign_name).filter(Boolean))).map(
        name => ({
          value: name,
          label: translateDynamicLabel(name) || name,
        })
      ),
    ],
    [scopedCampaigns, translateDynamicLabel, t.filterAllCampaigns]
  );

  const sourceValues = useMemo(
    () => collectFilterOptions(scopedCampaigns, getCampaignSource),
    [scopedCampaigns]
  );

  const sourceOptions = useMemo(
    () => [
      { value: "", label: t.filterAllSources },
      { value: "WhatsApp", label: t.whatsapp },
      { value: "Email", label: t.email },
    ],
    [t.filterAllSources, t.whatsapp, t.email]
  );

  const statusOptions = useMemo(
    () => [
      { value: "", label: t.filterAllStatus },
      { value: "scheduled", label: t.scheduled },
      { value: "draft", label: t.draft },
      { value: "unscheduled", label: t.unscheduled },
    ],
    [t.filterAllStatus, t.scheduled, t.draft, t.unscheduled]
  );

  // "What DataTram has helped you achieve" — same endpoint/mapper the
  // Engagements Overview uses, scoped to this page's date range + campaign filter.
  const achievementsRequest = useCallback(
    () =>
      engagementOverviewService.fetchAchievements({
        start_date: startDate,
        end_date: endDate,
        campaign_name: campaignFilter || null,
      }),
    [startDate, endDate, campaignFilter, lang]
  );

  const achievements = useAsyncResource<EngagementAchievementsData>(
    startDate && endDate ? achievementsRequest : null,
    t.achievementsLoadFailed
  );

  const businessImpact = useMemo(
    () => mapAchievements(achievements.data),
    [achievements.data]
  );

 
  const comparisonRequest = useCallback(
    () =>
      campaignPerformanceService.fetchCampaignComparison({
        start_date: startDate,
        end_date: endDate,
        source: null,
        campaign_name: campaignFilter || null,
      }),
    [startDate, endDate, campaignFilter, lang]
  );

  const comparison = useAsyncResource<CampaignComparisonData>(
    startDate && endDate ? comparisonRequest : null,
    t.errorFetchingCampaigns
  );

  const narrativeData = useMemo(() => {
    const blocks: Record<string, unknown> = {};
    if (achievements.data) {
      blocks.achievements = achievements.data;
    }
    if (filteredCampaigns.length > 0) {

      blocks.campaigns_list = {
        data: filteredCampaigns,
        pagination: listPagination,
      };
    }
    if (comparison.data) {
      blocks.comparison = comparison.data;
    }
    return blocks;
  }, [achievements.data, filteredCampaigns, listPagination, comparison.data]);

  const {
    narrative,
    loading: narrativeLoading,
    aiSummary,
  } = useDashboardNarrative({
    dashboardKey: "engagements_campaigns",
    tabKey: "overview",
    startDate,
    endDate,
    data: narrativeData,
    enabled: Boolean(achievements.data) || filteredCampaigns.length > 0,
  });


  const comparisonCampaigns = useMemo(() => {
    const rows = comparison.data?.campaigns ?? [];
    return rows.filter(row => {
      if (comparisonIds.length > 0 && !comparisonIds.includes(row.campaign_id)) {
        return false;
      }
      if (
        comparisonSources.length > 0 &&
        !comparisonSources.some(
          source => source.toLowerCase() === String(row.source ?? "").toLowerCase()
        )
      ) {
        return false;
      }
      return true;
    });
  }, [comparison.data, comparisonIds, comparisonSources]);

  const filteredChartData = useMemo(() => {
    return comparisonCampaigns.map(row => ({
      name: translateDynamicLabel(row.campaign_name) || "-",
      delivered: readNumericValue(row.delivered) ?? 0,
      deliveredFormatted: readFormattedText(row.deliveredFormattedValue) ?? readFormattedText(row.delivered) ?? "",
      openRate: readNumericValue(row.open_rate) ?? 0,
      openRateFormatted: readFormattedText(row.open_rateFormattedValue) ?? readFormattedText(row.open_rate) ?? "",
      clickRate: readNumericValue(row.ctr) ?? 0,
      clickRateFormatted: readFormattedText(row.ctrFormattedValue) ?? readFormattedText(row.ctr) ?? "",
      bounceRate: readNumericValue(row.bounce_rate) ?? 0,
      bounceRateFormatted: readFormattedText(row.bounce_rateFormattedValue) ?? readFormattedText(row.bounce_rate) ?? "",
      conversionRate: readNumericValue(row.conversion_rate) ?? 0,
      conversionRateFormatted: readFormattedText(row.conversion_rateFormattedValue) ?? readFormattedText(row.conversion_rate) ?? "",
      unsubscribeRate: readNumericValue(row.unsubscribe_rate) ?? 0,
      unsubscribeRateFormatted: readFormattedText(row.unsubscribe_rateFormattedValue) ?? readFormattedText(row.unsubscribe_rate) ?? "",
    }));
  }, [comparisonCampaigns, translateDynamicLabel]);

  const comparisonOptions = useMemo(
    () =>
      scopedCampaigns
        .filter(campaign => getDraftId(campaign) === null)
        .map(campaign => ({
          value: campaign.campaign_id,
          label: translateDynamicLabel(campaign.campaign_name) || campaign.campaign_name,
        })),
    [scopedCampaigns, translateDynamicLabel]
  );

  const comparisonSourceOptions = useMemo(
    () =>
      sourceValues.map(value => ({
        value,
        label: translateDynamicLabel(value) || value,
      })),
    [sourceValues, translateDynamicLabel]
  );


const fetchCampaignsOverview = async (start: string, end: string) => {
    if (!start || !end) {return;}
  try {
    setLoading(true);

    const { data, pagination } = await campaignPerformanceService.fetchCampaignsList({
      start_date: start,
      end_date: end,
      // Both scopes in one pass — the Active/Archive tab filters client-side.
      status: null,
      source: null,
      page: 1,
      page_size: CAMPAIGN_LIST_PAGE_SIZE,
      sort_by: "sent_at",
      sort_order: "desc",
    });

    const mapped = data.map(mapCampaignListRow);
    setCampaigns(mapped);
    setListPagination(pagination ?? null);

    localStorage.setItem(
      "campaignInfo",
      JSON.stringify(
        data
          .filter(campaign => !campaign.draft_id)
          .map(campaign => ({ id: campaign.campaign_id, name: campaign.campaign_name })),
      ),
    );
  } catch (err) {
    console.error(t.errorFetchingCampaigns, err);
    setCampaigns([]);
    setListPagination(null);
  } finally {
    setLoading(false);
  }
};
  const navigate = useNavigate();
  const [resumingDraftId, setResumingDraftId] = useState<number | null>(null);

  const handleResumeDraft = async (campaign: Campaign) => {
    const draftId = getDraftId(campaign);
    if (draftId === null || resumingDraftId !== null) {
      return;
    }
    setResumingDraftId(draftId);
    try {
      const draft = await CampaignDraftAPI.fetch(draftId);
      if (draft.channel === "whatsapp") {
        message.info(t.whatsappDraftUnavailable);
        return;
      }
      navigate(getDraftEditorRoute(draft), { state: { draft } });
    } catch (err) {
      message.error(getThrownApiDetail(err) || t.draftOpenFailed);
      fetchCampaignsOverview(startDate, endDate);
    } finally {
      setResumingDraftId(null);
    }
  };

  const handleCampaignClick = (campaign: Campaign) => {
    if (getDraftId(campaign) !== null) {
      handleResumeDraft(campaign);
      return;
    }
    setPerformanceCampaign({
      campaign_id: campaign.campaign_id,
      campaign_name: campaign.campaign_name,
    });
  };


  const handleViewCampaign = async (campaign: Campaign) => {
    const source = getCampaignSource(campaign);
    const draftId = getDraftId(campaign);
    let html: string | undefined;

    if (draftId !== null) {
      try {
        html = (await CampaignDraftAPI.fetch(draftId)).content?.html || undefined;
      } catch (err) {
        message.error(getThrownApiDetail(err) || t.draftOpenFailed);
        return;
      }
      if (!html) {
        message.info(t.campaignNotGenerated);
        return;
      }
    }

    setViewCampaign({
      campaignId: campaign.campaign_id,
      name:
        translateDynamicLabel(campaign.campaign_name) ||
        campaign.campaign_name ||
        (draftId !== null ? t.untitledCampaign : ""),
      source,
      sourceLabel: getCampaignSourceLabel(campaign, t, translateDynamicLabel),
      sourceVariant: normalizeBadgeVariant(source),
      html,
    });
  };

  const handleDeleteCampaign = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setPendingActionIds([campaign.campaign_id]);
    setActionType("delete");
    setModalOpen(true);
  };

  const handleArchiveCampaign = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setPendingActionIds([campaign.campaign_id]);
    setActionType(campaign.status === "archive" ? "unarchive" : "archive");
    setModalOpen(true);
  };

  const handleCancel = () => {
    setModalOpen(false);
    setSelectedCampaign(null);
    setActionType(null);
  };

  const handleConfirm = async () => {
    if (!actionType) {return;}
    const campaignIds =
      pendingActionIds.length > 0
        ? pendingActionIds
        : selectedCampaign
          ? [selectedCampaign.campaign_id]
          : [];
    if (campaignIds.length === 0) {return;}
    setActionLoading(true);
    try {
      const isDelete = actionType === "delete";
      const isArchive = actionType === "archive";

      // WhatsApp ids come back in `not_found` — archive/delete do not cover
      // that channel yet, so report them rather than claiming success.
      const notFound = isDelete
        ? (await campaignPerformanceService.deleteCampaigns({ campaign_id: campaignIds }))
            .not_found
        : (
            await campaignPerformanceService.archiveCampaigns({
              campaign_id: campaignIds,
              archived: isArchive,
            })
          ).not_found;

      const actioned = campaignIds.filter(id => !notFound?.includes(id));

      if (notFound?.length) {
        message.warning(
          t.failedToActionCampaign.replace(
            "{{action}}",
            isDelete ? t.delete : isArchive ? t.archive : t.unarchive,
          ),
        );
      }

      if (actioned.length > 0) {
        if (actioned.length === 1) {
          const name =
            translateDynamicLabel(selectedCampaign?.campaign_name) ||
            translateDynamicLabel(campaigns.find(c => c.campaign_id === campaignIds[0])?.campaign_name) ||
            t.defaultCampaignName;
          message.success(
            isDelete
              ? t.campaignDeletedSuccess.replace("{{name}}", name)
              : isArchive
                ? t.campaignArchivedSuccess.replace("{{name}}", name)
                : t.campaignUnarchivedSuccess.replace("{{name}}", name),
          );
        } else {
          message.success(
            (isDelete
              ? t.campaignsDeletedSuccess
              : isArchive
                ? t.campaignsArchivedSuccess
                : t.campaignsUnarchivedSuccess
            ).replace("{{count}}", String(actioned.length)),
          );
        }

        fetchCampaignsOverview(startDate, endDate);
      } else if (!notFound?.length) {
        const actionLabel = isDelete ? t.delete : isArchive ? t.archive : t.unarchive;
        message.error(t.failedToActionCampaign.replace("{{action}}", actionLabel));
      }
    } catch (err) {
      console.error(err);
      const actionLabel =
        actionType === "delete"
          ? t.delete
          : actionType === "archive"
            ? t.archive
            : t.unarchive;
      message.error(t.errorActioningCampaign.replace("{{action}}", actionLabel));
    } finally {
      setActionLoading(false);
      setModalOpen(false);
      setSelectedCampaign(null);
      setPendingActionIds([]);
      setSelectedCampaigns([]);
      setActionType(null);
    }
  };

// ðŸŸ¦ Button handler
const handleExport = () => {
  // If some campaigns are selected â†’ export those only
  const campaignsToExport =
    selectedCampaigns.length > 0
      ? filteredCampaigns.filter(c =>
          selectedCampaigns.includes(c.campaign_id)
        )
      : filteredCampaigns; // If none selected, export all

  exportToCSV(campaignsToExport, t.exportFilename);
};
const handleExportSingle = (campaign: Campaign) => {
  exportToCSV([campaign], `${t.singleExportFilename}${campaign.campaign_id}`);
};
const handleBulkDelete = () => {
  if (selectedCampaigns.length === 0) {
    message.info(t.selectAtLeastOneDelete);
    return;
  }
  setSelectedCampaign(null);
  setPendingActionIds(selectedCampaigns);
  setActionType("delete");
  setModalOpen(true);
};

const handleBulkArchive = () => {
  if (selectedCampaigns.length === 0) {
    message.info(t.selectAtLeastOneArchive);
    return;
  }
  const archivableIds = selectedCampaigns.filter(id => !isDraftCampaignId(id));
  if (archivableIds.length < selectedCampaigns.length) {
    message.info(t.draftsCannotBeArchived);
  }
  if (archivableIds.length === 0) {
    return;
  }
  const selectedSet = new Set(archivableIds);
  const selectedRecords = campaigns.filter(c => selectedSet.has(c.campaign_id));
  const allArchived =
    selectedRecords.length > 0 && selectedRecords.every(c => c.status === "archive");
  setSelectedCampaign(null);
  setPendingActionIds(archivableIds);
  setActionType(allArchived ? "unarchive" : "archive");
  setModalOpen(true);
};

const searchedCampaigns = useMemo(() => {
  const normalizedQuery = searchQuery.toLowerCase();
  return filteredCampaigns.filter(c =>
    c.campaign_name.toLowerCase().includes(normalizedQuery) ||
    (translateDynamicLabel(c.campaign_name) || "")
      .toLowerCase()
      .includes(normalizedQuery) ||
    (translateDynamicLabel(c.audience) || "")
      .toLowerCase()
      .includes(normalizedQuery)
  );
}, [filteredCampaigns, searchQuery, translateDynamicLabel]);

const totalPages = Math.max(1, Math.ceil(searchedCampaigns.length / pageSize) || 1);

const selectableCampaignIds = useMemo(
  () => searchedCampaigns.map(c => c.campaign_id),
  [searchedCampaigns]
);

const selectedInScopeCount = useMemo(
  () => selectableCampaignIds.filter(id => selectedCampaigns.includes(id)).length,
  [selectableCampaignIds, selectedCampaigns]
);

const allInScopeSelected =
  selectableCampaignIds.length > 0 &&
  selectedInScopeCount === selectableCampaignIds.length;


useEffect(() => {
  if (selectAllRef.current) {
    // Only show checked when all rows are selected; never show indeterminate state.
    selectAllRef.current.indeterminate = false;
  }
}, [selectedInScopeCount, selectableCampaignIds.length]);

const columns = buildCampaignTableColumns({
  text: t,
  selectAllRef,
  allInScopeSelected,
  selectableCampaignIds,
  selectedCampaigns,
  setSelectedCampaigns,
  translateDynamicLabel,
  formatDateTime,
  onOpen: handleCampaignClick,
  onView: handleViewCampaign,
  onEdit: handleResumeDraft,
  onArchive: handleArchiveCampaign,
  onDelete: handleDeleteCampaign,
  onExport: handleExportSingle,
});

  // Effects
useEffect(() => {
  fetchCampaignsOverview(startDate, endDate);
}, [lang]);

  useEffect(() => {
    document.body.style.overflowY = "auto";
    return () => {
      document.body.style.overflowY = "unset";
    };
  }, []);

  // Narrowing the result set can leave the current page past the end of it.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, showArchived, campaignFilter, sourceFilter, statusFilter]);

  // Source/Status/comparison options are scope-derived, so a value carried over
  // from the other tab would filter everything out. Clear them on tab change.
  useEffect(() => {
    setSourceFilter("");
    setStatusFilter("");
    setComparisonIds([]);
    setComparisonSources([]);
  }, [showArchived]);

  const { title, icon, confirmText, loadingText } = getModalProps(
    actionType || "",
    t,
  );
  const actionLabel =
    actionType === "delete"
      ? t.delete
      : actionType === "archive"
        ? t.archive
        : t.unarchive;
  const confirmDescription = (() => {
    if (!actionType) {return "";}
    if (pendingActionIds.length > 1) {
      return t.confirmMany
        .replace("{{action}}", actionLabel)
        .replace("{{count}}", String(pendingActionIds.length));
    }
    const singleId =
      pendingActionIds[0] || selectedCampaign?.campaign_id || "";
    const singleName =
      translateDynamicLabel(selectedCampaign?.campaign_name) ||
      translateDynamicLabel(campaigns.find(c => c.campaign_id === singleId)?.campaign_name) ||
      t.thisCampaign;
    return t.confirmOne
      .replace("{{action}}", actionLabel)
      .replace("{{name}}", singleName);
  })();
  return (
    <div className="campaign-dashboard" dir={isArabic ? "rtl" : "ltr"}>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="dashboard-header">
            <div className="campaign-dashboard__title-row">
              <div className="campaign-dashboard__title-copy">
                <p className="Campaign-dashboard-header">{t.campaignPerformance}</p>
                <div className="audience-summary">
                  <span>{t.trackEmailCampaigns}</span>
                </div>
              </div>
              <CreateCampaignMenu />
              <div className="campaign-dashboard__filter-select">
                <SelectMenu
                  value={campaignFilter}
                  options={campaignNameOptions}
                  onChange={setCampaignFilter}
                  ariaLabel={t.filterCampaignName}
                  className="campaign-dashboard__select"
                  dropdownClassName="campaign-dashboard__select-dropdown"
                />
              </div>
              <div className="campaign-dashboard__filters">
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  maxPastMonths={6}
                  onRangeChange={({ start, end }) => {
                    setStartDate(start);
                    setEndDate(end);
                    fetchCampaignsOverview(start, end);
                  }}
                />
              </div>
            </div>

           
          </div>

          <DashboardNarrativePanel
            narrative={narrative}
            loading={narrativeLoading}
            aiSummary={aiSummary}
            defaultCollapsed={false}
          />

          <div className="campaign-dashboard__achievements">
            <BusinessImpactSection
              metrics={businessImpact}
              subtitle={t.achievementsSubtitle}
              trendFractionDigits={2}
            />
          </div>

          <div className="campaign-performance-card">
            <div className="campaign-card-header">
              <div className="campaign-card-title">
                {showArchived ? t.archivedCampaigns : t.activeCampaigns}
              </div>
              <div className="campaign-card-actions">
                <button
                  className="campaign-action-btn"
                  type="button"
                  onClick={handleExport}
                >
                  <Download size={15} />
                  {t.export}
                </button>
                <button
                  className="campaign-action-btn"
                  type="button"
                  onClick={handleBulkArchive}
                >
                  <Archive size={15} />
                  {showArchived ? t.unarchive : t.archive}
                </button>
                <button
                  className="campaign-action-btn campaign-action-btn--danger"
                  type="button"
                  onClick={handleBulkDelete}
                >
                  <Trash2 size={15} />
                  {t.delete}
                </button>
              </div>
            </div>

            <div className="campaign-card-toolbar">
              <div className="campaign-toolbar-left">
                <div
                  className="campaign-scope-tabs"
                  role="tablist"
                  aria-label={t.activeCampaigns}
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={!showArchived}
                    className={`campaign-scope-tab ${!showArchived ? "is-active" : ""}`}
                    onClick={() => setShowArchived(false)}
                  >
                    {t.tabActive}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={showArchived}
                    className={`campaign-scope-tab ${showArchived ? "is-active" : ""}`}
                    onClick={() => setShowArchived(true)}
                  >
                    {t.tabArchive}
                  </button>
                </div>
              </div>
              <div className="campaign-toolbar-right">
                <StyledInput
                  placeholder={t.search}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="campaign-search-input"
                />
                <SelectMenu
                  value={sourceFilter}
                  options={sourceOptions}
                  onChange={setSourceFilter}
                  ariaLabel={t.filterSource}
                  className="campaign-dashboard__select campaign-dashboard__select--toolbar"
                  dropdownClassName="campaign-dashboard__select-dropdown"
                />
                <SelectMenu
                  value={statusFilter}
                  options={statusOptions}
                  onChange={setStatusFilter}
                  ariaLabel={t.filterStatus}
                  className="campaign-dashboard__select campaign-dashboard__select--toolbar"
                  dropdownClassName="campaign-dashboard__select-dropdown"
                />
              </div>
            </div>

            <CampaignTable
              key={lang}
              columns={columns}
              data={searchedCampaigns.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
              rowKey="campaign_id"
              loading={loading}
              className="campaign-table"
              expandable={{
                expandedRowRender: (record: Campaign) => (
                  <CampaignMetricsPanel
                    record={record}
                    text={t}
                    formatInteger={formatInteger}
                    formatPercentValue={formatPercentValue}
                  />
                ),
                rowExpandable: record => getDraftId(record) === null,
                expandRowByClick: false,
                expandIcon: ({ expanded, onExpand, record }) =>
                  getDraftId(record) === null ? (
                    <button
                      type="button"
                      className={`campaign-table__expand-icon ${
                        expanded ? "campaign-table__expand-icon--open" : ""
                      }`}
                      onClick={event => onExpand(record, event)}
                      aria-label={expanded ? t.collapseRow : t.expandRow}
                      aria-expanded={expanded}
                    >
                      <ChevronRight size={16} />
                    </button>
                  ) : null,
              }}
              customPagination={
                <CampaignTablePagination
                  text={t}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  formatInteger={formatInteger}
                  onPageChange={setCurrentPage}
                />
              }
            />
          </div>

          <CampaignComparisonChart
            text={t}
            chartData={filteredChartData}
            selectedMetrics={selectedMetrics}
            metricOptions={metricOptions}
            campaignOptions={comparisonOptions}
            sourceOptions={comparisonSourceOptions}
            campaignIds={comparisonIds}
            sources={comparisonSources}
            onCampaignIdsChange={setComparisonIds}
            onSourcesChange={setComparisonSources}
            formatPercentValue={formatPercentValue}
          />

          <ConfirmationModal
            modalOpen={modalOpen}
            onCancel={handleCancel}
            onConfirm={handleConfirm}
            title={title}
            description={confirmDescription}
            icon={icon}
            confirmText={
              actionLoading ? (
                <>
                  <SyncOutlined spin /> {loadingText}
                </>
              ) : (
                confirmText
              )
            }
            cancelText={t.cancel}
          />
          <CampaignViewModal
            campaign={viewCampaign}
            onClose={() => setViewCampaign(null)}
          />
          <CampaignPerformance
            open={Boolean(performanceCampaign)}
            onClose={() => setPerformanceCampaign(null)}
            campaignId={performanceCampaign?.campaign_id}
            campaignName={performanceCampaign?.campaign_name}
          />
        </>
        
      )}
    </div>
  );
};

export default CampaignDashboard;
